<script>
    import profile from "../../stores/profile.js";
    import route from "../../stores/route.js";
    import windows from "../../stores/windows.js";
    import Button from "../windows/Button.svelte";
    import Steps from "./onboarding/Steps.svelte";
    import Imagery from "./onboarding/Imagery.svelte";
    import Progress from "./onboarding/Progress.svelte";
    import isMobile from "../../helpers/mobile.js";
    import onboarding from "../../stores/onboarding.js";
    import { sleep } from "../../helpers/time.js";

    const NUMBER_OF_STEPS = 10;
    const INDEX_FOR_PROFILE_SELECTION_STEP = 2;

    let indexForCurrentStep = 0;
    let didSkipIntro = false;
    let selectedProfile;
    let progress = $route ? 1.0 : 0.0;
    let progressDescription;
    let indexForCurrentImage = 1;
    let imageRotationInterval;

    $: image = `/onboarding/america-os-${indexForCurrentImage}.jpeg`;

    $: canRetreat =
        indexForCurrentStep !== INDEX_FOR_PROFILE_SELECTION_STEP + 1 &&
        indexForCurrentStep > 0;

    $: canAdvance = indexForCurrentStep < NUMBER_OF_STEPS - 1 || $route;

    function didAdvance() {
        const didReachLastStep = indexForCurrentStep === NUMBER_OF_STEPS - 1;

        if (didReachLastStep) {
            imageRotationInterval = setInterval(function rotateImage() {
                if (indexForCurrentImage === 15) {
                    clearInterval(imageRotationInterval);
                    return;
                }

                indexForCurrentImage += 1;
            }, 5000);
        }
    }

    function didClickBackButton() {
        indexForCurrentStep -= 1;
        indexForCurrentImage = indexForCurrentStep - 1;
    }

    async function didClickNextButton() {
        indexForCurrentStep += 1;
        indexForCurrentImage = indexForCurrentStep + 1;

        didAdvance();

        if (!$profile && selectedProfile) {
            profile.set(selectedProfile);
        }

        const didCompleteOnboarding = indexForCurrentStep === NUMBER_OF_STEPS;

        if (didCompleteOnboarding) {
            clearInterval(imageRotationInterval);
            onboarding.complete({ did_skip_intro: didSkipIntro });

            $windows.forEach((window) => {
                if (window.kind === "onboarding") {
                    windows.close(window.id);
                }
            });

            const { innerWidth, innerHeight } = window;

            const info = windows.open("info");
            windows.move(
                info,
                innerWidth * 0.66 - 137,
                innerHeight * 0.66 - 57
            );

            await sleep(100 + Math.random() * 200);

            if (isMobile) {
                windows.open("map");
            } else {
                windows.focus(info);

                const radio = windows.open("radio");
                windows.move(
                    radio,
                    innerWidth * 0.66 - 125,
                    innerHeight * 0.33 - 52
                );
                windows.focus(radio);

                await sleep(100 + Math.random() * 200);

                const map = windows.open("map");
                windows.move(
                    map,
                    innerWidth * 0.33 - 150,
                    innerHeight * 0.5 - 150
                );
                windows.focus(map);
            }
        }
    }

    function didSelectProfile(e) {
        selectedProfile = e.detail.profile;
    }

    function didRouteProgress(e) {
        progress = e.detail.progress * 0.85;
        progressDescription = e.detail.description;
    }

    function didClickSkipButton(e) {
        e.preventDefault();
        didSkipIntro = true;
        indexForCurrentStep = NUMBER_OF_STEPS - 1;
        indexForCurrentImage = indexForCurrentStep + 1;
        didAdvance();
    }

    $: $route && (progress = 1.0);
</script>

<svelte:window on:route:progress={didRouteProgress} />

<div class="onboarding">
    <div class="content">
        <div class="imagery">
            <Imagery {image} />
        </div>
        <div class="body">
            <Steps index={indexForCurrentStep} on:profile={didSelectProfile} />
            {#if indexForCurrentStep === NUMBER_OF_STEPS - 1}
                <div style="height: 1.5em" />
                <Progress {progress} />
                <div class="counter">
                    7 / 7 &middot;
                    {$route
                        ? "Done"
                        : progressDescription || "Setting up your journey"}
                </div>
            {/if}
            {#if indexForCurrentStep === 0}
                <div class="credits">
                    Foreword <span>& photography </span>by
                    <a
                        href="https://www.keatonventura.com"
                        target="_blank"
                        rel="noreferrer">Keaton Ventura</a
                    >.
                </div>
            {/if}
            <div class="flexible" />
            <div class="navigation">
                {#if indexForCurrentStep > 2 && indexForCurrentStep < NUMBER_OF_STEPS - 1}
                    <a href="skip" on:click={didClickSkipButton}>Skip Intro</a>
                {/if}
                <div class="flexible" />
                <Button isEnabled={canRetreat} action={didClickBackButton}>
                    Go Back
                </Button>
                <Button isEnabled={canAdvance} action={didClickNextButton}
                    >Next</Button
                >
            </div>
        </div>
    </div>
</div>

<style>
    .onboarding {
        display: flex;
        position: relative;
        flex-direction: column;
        height: 100%;
    }

    :global(.imagery::after) {
        position: absolute;
        z-index: -1;
        width: 0;
        height: 0;
        overflow: hidden;
        content: url("/onboarding/america-os-1.jpeg")
            url("/onboarding/america-os-2.jpeg")
            url("/onboarding/america-os-3.jpeg")
            url("/onboarding/america-os-4.jpeg")
            url("/onboarding/america-os-5.jpeg")
            url("/onboarding/america-os-6.jpeg")
            url("/onboarding/america-os-7.jpeg")
            url("/onboarding/america-os-8.jpeg")
            url("/onboarding/america-os-9.jpeg")
            url("/onboarding/america-os-10.jpeg")
            url("/onboarding/america-os-11.jpeg")
            url("/onboarding/america-os-12.jpeg")
            url("/onboarding/america-os-13.jpeg")
            url("/onboarding/america-os-14.jpeg")
            url("/onboarding/america-os-15.jpeg");
    }

    .content {
        display: flex;
        flex-grow: 1;
        flex-direction: row;
        padding: var(--spacing);
    }

    .imagery {
        position: relative;
        border-top: var(--border-color-dark) var(--chrome-depth) inset;
        border-right: var(--border-color-light) var(--chrome-depth) inset;
        border-bottom: var(--border-color-light) var(--chrome-depth) inset;
        border-left: var(--border-color-dark) var(--chrome-depth) inset;
        min-width: 38.2%;
        overflow: hidden;
    }

    @media only screen and (max-width: 734px) {
        .imagery {
            display: none;
        }
    }

    .body {
        display: flex;
        flex-direction: column;
        padding: calc(var(--spacing) * 3);
        padding-left: calc(var(--spacing) * 4);
    }

    @media only screen and (max-width: 734px) {
        .body {
            width: 100%;
        }
    }

    .body > .flexible {
        flex-grow: 1;
    }

    .navigation {
        display: flex;
        gap: var(--spacing);
        box-sizing: border-box;
        width: 100%;
    }

    .navigation a {
        position: relative;
        top: 4px;
        color: #666666;
    }

    .credits {
        opacity: 0.5;
        margin-top: 1.5em;
    }

    .credits a {
        color: #000000;
    }

    @media only screen and (max-width: 734px) {
        .credits span {
            display: none;
        }
    }

    .flexible {
        flex-grow: 1;
    }

    .counter {
        margin-top: 15px;
    }
</style>
